import { CURRENCY_LIST } from './currency-metadata.generated';

export { CURRENCY_LIST };

/** Currency code as an uppercase string (ISO 4217 or token ticker). */
export type CurrencyCode = string;

export type CurrencyKind = 'fiat' | 'crypto' | 'metal' | 'other';

export interface CurrencyRecord {
  code: string;
  name: string;
  symbol: string | null;
  /** ISO 3166-1 alpha-2, lowercase. Explicit allowlist only — null otherwise. */
  countryCode: string | null;
  /** Display country name; present on the top fiat rows for search tiering. */
  country?: string;
  /** Lowercase, spaceless search variants (see codegen). */
  aliases: string[];
  /** Precomputed CSS hook, or null when there is no countryCode. */
  flagClass: string | null;
  kind: CurrencyKind;
}

export interface SearchEntry extends CurrencyRecord {
  _hay: string;
}

/** CSS hook for a currency's flag; null when the record has no countryCode. */
export function flagClass(record: Pick<CurrencyRecord, 'countryCode'>): string | null {
  const cc = record.countryCode;
  if (typeof cc !== 'string' || !/^[a-z]{2}$/.test(cc)) return null;
  return `currency-flag currency-flag-${cc}`;
}

const CODE_SET: Set<string> = new Set(CURRENCY_LIST.map((r) => r.code));

/** True when `code` (any case) is a known currency in the metadata table. */
export function isSupportedCurrency(code: string): boolean {
  return CODE_SET.has(code.toUpperCase());
}

/** Currencies pinned to the top of pickers and search tie-breaks. */
export const POPULAR_CURRENCIES: readonly string[] = ['USD', 'SAR', 'AED', 'EUR', 'EGP', 'GBP'];

/**
 * Common region (ISO 3166-1 alpha-2, uppercase) -> currency code.
 * Used to default the display currency from `navigator.language`.
 */
export const REGION_TO_CURRENCY: Record<string, string> = {
  US: 'USD',
  SA: 'SAR',
  AE: 'AED',
  EG: 'EGP',
  GB: 'GBP',
  UK: 'GBP',
  MA: 'MAD',
  DE: 'EUR',
  FR: 'EUR',
  ES: 'EUR',
  IT: 'EUR',
  NL: 'EUR',
  BE: 'EUR',
  IE: 'EUR',
  PT: 'EUR',
  GR: 'EUR',
  AT: 'EUR',
  FI: 'EUR',
  QA: 'QAR',
  KW: 'KWD',
  BH: 'BHD',
  OM: 'OMR',
  JO: 'JOD',
  LB: 'LBP',
  IQ: 'IQD',
  DZ: 'DZD',
  TN: 'TND',
  LY: 'LYD',
  SD: 'SDG',
  SY: 'SYP',
  YE: 'YER',
  TR: 'TRY',
  PK: 'PKR',
  IN: 'INR',
  BD: 'BDT',
  PH: 'PHP',
  ID: 'IDR',
  MY: 'MYR',
  JP: 'JPY',
  KR: 'KRW',
  CN: 'CNY',
  CA: 'CAD',
  AU: 'AUD',
  CH: 'CHF',
  SE: 'SEK',
  NO: 'NOK',
  DK: 'DKK',
  BR: 'BRL',
  MX: 'MXN',
  ZA: 'ZAR',
  NG: 'NGN',
  IL: 'ILS',
  RU: 'RUB',
};
